import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AtMessageModule, IconModule, LaunchDialogService, MediaModule, OutletModule, ProductListItemContext, ProductListItemContextSource, ProductListOutlets, SpinnerModule } from '@spartacus/storefront';
import { UserAccountFacade } from "@spartacus/user/account/root";
import { filter, take, takeUntil, tap } from "rxjs/operators";
import { Subject } from "rxjs";
import { I18nModule, Product, TranslationService, UrlModule, User, WindowRef } from "@spartacus/core";
import { normalizeSlug } from '../../../../shared/helpers/slug.helper';
import { TooltipPosition } from 'src/app/generac/enums/tooltip-position.enum';
import { GenUnavailabilityDetailType } from 'src/app/generac/enums/unavailability-detail-type.enum';
import { GenContactFactoryInfo } from 'src/app/generac/models/contact-info.model';
import { GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { GenSpIconsComponent } from "src/app/generac/shared/components/sp-icons/sp-icons.component";
import { GenInfoPriceWrapperComponent } from 'src/app/generac/shared/components/info-price-wrapper/info-price-wrapper.component';
import { SafeModule } from 'src/app/generac/shared/pipes/safe/safe.module';
import { GenSharedModule } from 'src/app/generac/shared/shared.module';
import { GenAddToCartModule } from '../../../cart/product-add-to-cart/product-add-to-cart.module';
import { GenAddToFavoritesComponent } from '../../../favorites/add-to-favorites/add-to-favorites.component';
import { GenProductPurchaseRestrictionMessageComponent } from '../../product-purchase-restriction-message/product-purchase-restriction-message.component';
import { GenInfoMessageComponent } from 'src/app/generac/shared/components/info-message/info-message.component';
import { GenProductBadgeComponent } from '../../../../shared/components/product-badge/product-badge.component';

@Component({
  selector: 'gen-product-grid-item',
  templateUrl: './product-grid-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ProductListItemContextSource,
    {
      provide: ProductListItemContext,
      useExisting: ProductListItemContextSource,
    },
  ],
  imports: [
    CommonModule,
    MediaModule,
    RouterModule,
    GenInfoMessageComponent,
    OutletModule,
    UrlModule,
    AtMessageModule,
    I18nModule,
    GenSharedModule,
    SpinnerModule,
    IconModule,
    SafeModule,
    GenAddToFavoritesComponent,
    GenInfoPriceWrapperComponent,
    GenSpIconsComponent,
    GenProductPurchaseRestrictionMessageComponent,
    GenAddToCartModule,
    GenProductBadgeComponent,
  ],
})

export class GenProductGridItemComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  readonly ProductListOutlets = ProductListOutlets;

  @Input() isSubs = false;
  @Input() isAddModal = false;

  _phoneNumberOrEmail: string;
  get phoneNumberOrEmail(): string {
    return this._phoneNumberOrEmail;
  }
  @Input() set phoneNumberOrEmail(value: string) {
    this.contactForPricingHref = value?.includes('@') ? `mailto: ${value}` : `tel: ${value}`;
    this._phoneNumberOrEmail = value;
  }
  @Input() tooltipMessage: string;
  @Input() gaProductCategories: { name: string }[];
  @Input() gaListName: string;
  @Input() gaEntrySource: string;

  callForPricingMessage = '';
  contactForPricingHref: string;
  TooltipPosition = TooltipPosition;
  isServicePartsEnabled = false;
  showMoreNotes: boolean = true;
  materialNotesLength: number;

  _product: any;
  get product(): any {
    return this._product;
  }
  @Input() set product(value: any) {
    if (this.isSubs) {
      const product = value.images?.find((i: any) => i.format === 'product');
      const thumbnail = value.images?.find((i: any) => i.format === 'thumbnail');
      value.images = {
        PRIMARY: {
          product,
          thumbnail
        }
      };
      value.slug = normalizeSlug(value.name);
    }

    this._product = value;
  }

  @Output() addToCart: EventEmitter<Product> = new EventEmitter();
  @Output() onAddModalAdded = new EventEmitter<{ productCode: string, quantity: string }>();
  protected readonly GenUnavailabilityDetailType = GenUnavailabilityDetailType;
  private destroy$ = new Subject();
  private user: User;

  constructor(
    protected productListItemContextSource: ProductListItemContextSource,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
    protected translation: TranslationService,
    protected winRef: WindowRef,
    private userAccount: UserAccountFacade,
    private launchDialogService: LaunchDialogService,
    private elRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnChanges(changes?: SimpleChanges): void {
    if (changes?.product && !this.isSubs) {
      this.productListItemContextSource.product$.next(this.product);
    }
  }

  ngOnInit(): void {
    this.getUser();
    this.isServicePartsEnabled = JSON.parse(this.winRef.localStorage.getItem('isServicePartsEnabled'));
  }

  ngAfterViewInit(): void {
    const test = this.elRef.nativeElement.getElementsByClassName('sp-note');
    if (test.length > 0) {
      this.materialNotesLength = test[0].textContent.length;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  selectItem(item: Product): void {
    const product = {
      ...item,
      categories: this.gaProductCategories,
    };
    this.googleAnalyticsService.sendGaEvent('select_item', {
      items: [this.googleAnalyticsService.mapProductToGaItem(product, 0, undefined, this.gaListName, this.gaListName)],
      item_list_name: this.gaListName || '',
      item_list_id: this.gaListName ? this.gaListName.toLowerCase().trim().replace(/ /g, '_') : '',
      currency: product.price?.currencyIso,
    });
  }

  openContactFactory() {
    const contactFactoryInfo: GenContactFactoryInfo = {
      accountNumber: this.user.orgUnit.uid,
      companyName: this.user.orgUnit.name,
      supportPhoneNumber: '1-800-883-7535',
      subject: 'Service Part Contact Request',
      itemNumber: this.product.code,
      itemDescription: this.product.name
    };

    const dialog = this.launchDialogService.openDialog(
      'CONTACT_FACTORY',
      undefined,
      undefined,
      contactFactoryInfo
    );

    if (dialog) {
      dialog.pipe(take(1)).subscribe();
    }
    this.launchDialogService.dialogClose
      .pipe(takeUntil(this.destroy$))
      .subscribe()
  }

  toggleMoreLessNotes() {
    this.showMoreNotes = !this.showMoreNotes;
  }

  private getUser(): void {
    this.userAccount.get()
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        tap(user => this.user = user)
      )
      .subscribe()
  }

}
